import { all, call, put, select, takeLatest } from "redux-saga/effects";
import * as types from "../actions/actionTypes";
import {
  getAllBrandsResponse,
  getBrandByIdResponse,
  getBrandDashboardResponse,
  getBrandProfileResponse,
} from "../actions";
import { axiosGet } from "../../utils/common/axios";
import { createEntitySagas, extractPayloadAndCallback, notifyError, selectCompanyId, toQuery } from "./sagaHelpers";

export const brandEntity = createEntitySagas({
  entity: "brand",
  endpoint: "brands",
  label: "Brand",
  scope: "company",
  injectCompanyId: true,
  computedFields: ["walletBalance", "availableBalance", "heldAmount", "activeEngagements", "pendingActions", "lowBalance", "lastActivity"],
  selectSlice: (state) => state.brand,
  listKey: "brands",
  types: {
    getAll: types.GET_ALL_BRANDS,
    getById: types.GET_BRAND_BY_ID,
    create: types.CREATE_BRAND,
    update: types.UPDATE_BRAND,
    remove: types.DELETE_BRAND,
  },
  actions: { getAllResponse: getAllBrandsResponse, getByIdResponse: getBrandByIdResponse },
});

// A manufacturer's company has many brands under the same companyId (its clients), so listing
// "brands?companyId=X" and taking the first row does NOT identify which one is the signed-in
// brand - every brand but whichever was created first would see another brand's dashboard, team
// roster and settings. The signed-in brand's own id (captured at login in loginUser.brand.id) is
// the only reliable key, and companyId is still sent so the backend's tenant check on GET /{id}
// rejects it if it ever doesn't match.
function* getBrandProfileSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  try {
    const auth = yield select((state) => state.auth);
    const brandId = payload?.id ?? auth?.loginUser?.brand?.id;
    const companyId = yield* selectCompanyId(payload);
    if (!brandId) {
      yield put(getBrandProfileResponse(null));
      callback?.success?.(null);
      return;
    }
    const { data: profile } = yield call(axiosGet, `brands/${brandId}${toQuery({ companyId })}`);
    yield put(getBrandProfileResponse(profile));
    if (profile && payload?.withDashboard) {
      const res = yield call(axiosGet, `brands/${profile.id}/dashboard`);
      yield put(getBrandDashboardResponse(res.data));
    }
    callback?.success?.(profile);
  } catch (e) {
    notifyError(e, "Could not load brand profile");
    callback?.failure?.(e);
  }
}

function* getBrandDashboardSaga(action) {
  const { payload, callback } = extractPayloadAndCallback(action);
  try {
    const { data } = yield call(axiosGet, `brands/${payload.id}/dashboard`);
    yield put(getBrandDashboardResponse(data));
    callback?.success?.(data);
  } catch (e) {
    yield put(getBrandDashboardResponse(null));
    notifyError(e, "Could not load dashboard");
    callback?.failure?.(e);
  }
}

export function* brandSaga() {
  yield all([
    call(brandEntity.watcher),
    takeLatest(types.GET_BRAND_PROFILE, getBrandProfileSaga),
    takeLatest(types.GET_BRAND_DASHBOARD, getBrandDashboardSaga),
  ]);
}
